import React, { useState } from 'react';
import {
  ColumnPinningState,
  OnChangeFn,
  VisibilityState,
} from '@tanstack/react-table';
import { Columns3, GripVertical, Pin, RotateCcw } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

export interface ManagedColumn {
  id: string;
  label: string;
}

interface ColumnManagerPopoverProps {
  columns: ManagedColumn[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  columnOrder: string[];
  onColumnOrderChange: OnChangeFn<string[]>;
  columnPinning: ColumnPinningState;
  onColumnPinningChange: OnChangeFn<ColumnPinningState>;
  onReset?: () => void;
}

export const ColumnManagerPopover: React.FC<ColumnManagerPopoverProps> = ({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  columnPinning,
  onColumnPinningChange,
  onReset,
}) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const columnsById = new Map(columns.map(c => [c.id, c]));
  const orderedIds =
    columnOrder.length > 0 ? columnOrder : columns.map(c => c.id);

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const isPinned = (id: string) =>
    (columnPinning.left?.includes(id) ?? false) ||
    (columnPinning.right?.includes(id) ?? false);

  const toggleVisibility = (id: string) => {
    onColumnVisibilityChange(prev => ({
      ...prev,
      [id]: prev[id] === false,
    }));
  };

  // The UI only exposes a single pin toggle (no left/right choice), so
  // toggling always targets `left`. It still removes the id from `right`
  // first in case it got there some other way (e.g. a column configured
  // with defaultPinned: 'right') — otherwise a column could end up pinned on
  // both sides at once, which DataTable's sticky-positioning math can't
  // represent sensibly.
  const togglePin = (id: string) => {
    onColumnPinningChange(prev => {
      const left = prev.left ?? [];
      const right = prev.right ?? [];
      const currentlyPinned = left.includes(id) || right.includes(id);
      if (currentlyPinned) {
        return {
          left: left.filter(x => x !== id),
          right: right.filter(x => x !== id),
        };
      }
      // Regression: appending `id` to the end of `left` always pinned it
      // last, so unpinning then re-pinning the first column dropped it to
      // second place instead of restoring it to first. Rebuild `left` from
      // `orderedIds` (the user's actual column order) so a newly-pinned
      // column lands where it belongs relative to the other pinned columns,
      // not always at the end.
      const nextLeftIds = new Set([...left, id]);
      return {
        left: orderedIds.filter(x => nextLeftIds.has(x)),
        right: right.filter(x => x !== id),
      };
    });
  };

  const handleDragStart = (id: string) => (e: React.DragEvent) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('column-id', id);
  };

  const handleDragEnd = () => setDraggingId(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (targetId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    const fromId = e.dataTransfer.getData('column-id');
    if (!fromId || fromId === targetId) return;

    onColumnOrderChange(prev => {
      const base = prev.length > 0 ? [...prev] : orderedIds;
      const fromIdx = base.indexOf(fromId);
      const toIdx = base.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      base.splice(fromIdx, 1);
      base.splice(toIdx, 0, fromId);
      return base;
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="px-4 py-2 border border-[#e9eaeb] rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 bg-white h-auto w-auto gap-2"
        >
          <Columns3 className="w-4 h-4" />
          <span>Columns</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end">
        <div className="max-h-80 overflow-y-auto space-y-0.5">
          {orderedIds.map(id => {
            const column = columnsById.get(id);
            if (!column) return null;
            return (
              <div
                key={id}
                draggable
                onDragStart={handleDragStart(id)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop(id)}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50',
                  draggingId === id && 'opacity-50'
                )}
              >
                <GripVertical className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing shrink-0" />
                <Checkbox
                  id={`column-visible-${id}`}
                  checked={isVisible(id)}
                  onCheckedChange={() => toggleVisibility(id)}
                />
                <label
                  htmlFor={`column-visible-${id}`}
                  className="flex-1 text-sm text-gray-700 truncate cursor-pointer"
                >
                  {column.label}
                </label>
                <button
                  type="button"
                  onClick={() => togglePin(id)}
                  aria-label={
                    isPinned(id)
                      ? `Unpin ${column.label}`
                      : `Pin ${column.label}`
                  }
                  aria-pressed={isPinned(id)}
                  className={cn(
                    'p-1 rounded hover:bg-gray-100 shrink-0',
                    isPinned(id) ? 'text-primary-600' : 'text-gray-300'
                  )}
                >
                  <Pin className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
        {onReset && (
          <Button
            type="button"
            variant="ghost"
            onClick={onReset}
            className="w-full h-auto py-1.5 mt-2 text-sm text-gray-500 gap-1.5"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to default
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};
